import React from 'react';
import Markdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import styled from 'styled-components';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Header } from '@app/navigation/header';
import { AI_TOOLS, INPUT_KEYS } from '@constants';
import { advancedSettingsActionTypes } from '@constants/actionTypes';
import { ROUTES } from '@constants/routes';
import { AdvancedSettingsButton } from '@containers/DigitalAnalyst/AdvancedSettings';
import { AnswerBody } from '@features/result/components/AnswerBody';
import { MarkdownAnswerStyles } from '@features/result/styles/MarkdownAnswerStyles';
import {
  buildMarkdownImageLookup,
  getResponseImageAltText,
  getResponseImageSrc,
} from '@features/result/utils/markdownImages';
import { PrefilledQuestionInput } from '@features/query/form/PrefilledQuestionInput';
import { CsvDocAttachment } from '@features/dataAnalystAgent/CsvDocAttachment';
import { FeedDocAttachment } from '@features/dataAnalystAgent/FeedDocAttachment';
import { NoRetrievalToggle } from '@features/dataAnalystAgent/NoRetrievalToggle';
import { SessionPickerModal } from '@features/dataAnalystAgent/SessionPickerModal';
import { SourceFilterSection } from '@features/dataAnalystAgent/SourceFilterSection';
import {
  COPY as FEED_INPUT_COPY,
  hasInvalidFeedChip,
  liveFeedInputs,
  stripRows,
} from '@features/dataAnalystAgent/feedInputs';
import { invalidIdsFrom400, mapSubmitPayload } from '@features/dataAnalystAgent/submitPayload';
import { Body, Body1, Body5, Container, Page } from '@ui';
import { HeaderLine } from '@ui/HeaderLine';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FlatButton, SkeletonButton, TransparentFlatButton } from '@ui/buttons';
import { Working } from '@ui/Working';
import { AdvancedSettingsContext, AdvancedSettingsProvider } from '@/contexts/AdvancedSettingsContext';
import { FormStateContext, FormStateProvider } from '@/contexts/FormStateContext';
import { ModeContext } from '@/contexts/ModeContext';
import {
  SessionContext,
  SessionProvider,
  selectActiveLoadingTurn,
  selectContinueFromRecordId,
  selectIsResumed,
  selectResumedFromCreatedAt,
  selectResumedFromId,
} from '@/contexts/SessionContext';
import { SessionSummaryCard } from '@features/dataAnalystAgent/SessionSummaryCard';
import { FollowUpInput } from '@features/dataAnalystAgent/FollowUpInput';
import { addAdvancedSettingValues } from '@/hooks/application/useAdvancedSettings';
import { useDataAnalystAgentSessions } from '@hooks/features/useDataAnalystAgentSessions';
import { handle400Statuses, sendApiRequest } from '@/services/api';
import { BASEURL } from '@services/network';
import { createAuthedEventSource } from '@utils/streamAuth';
import { logError, logger, logNetRequest } from '@/utils/usefulFuncs';

const PageWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
`;

const HeadingWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 16px;
`;
const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const ResumeButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const FormActionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  text-align: center;
  padding-top: 24px;
`;
const SessionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;
const KickoffErrorWrapper = styled.div`
  padding: 12px 16px;
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  color: ${({ theme }) => theme.themeColors.error};
`;
const TurnsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
`;
const ResponseSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  table {
    margin-top: 16px;
    margin-bottom: 16px;
  }

  strong {
    display: inline-block;
    font-weight: 600;
    margin-bottom: 8px;
    padding-bottom: 8px;
  }

  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin-top: 16px;
    margin-bottom: 16px;
  }
`;
const ResponseCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
`;
const RequestValue = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;
const JsonValue = styled.pre`
  margin: 0;
  padding: 12px 16px;
  white-space: pre-wrap;
  word-break: break-word;
  overflow-x: auto;
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
  color: ${({ theme }) => theme.themeColors.text};
`;
const ImagesWrapper = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  align-items: start;
`;
const ImageCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  ${({ $isSingle }) =>
    $isSingle
      ? `
    grid-column: 1 / -1;
    width: 100%;
    max-width: 960px;
    justify-self: center;
  `
      : ''}
`;
const ResponseImage = styled.img`
  display: block;
  width: 100%;
  max-width: 100%;
  height: auto;
  max-height: ${({ $isSingle }) => ($isSingle ? '420px' : '320px')};
  object-fit: contain;
  border: 1px solid ${({ theme }) => theme.themeColors.border};
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.appBackground};
`;
const ErrorText = styled.div`
  color: ${({ theme }) => theme.themeColors.error};
`;
const AdditionalFieldsToggleWrapper = styled.div`
  display: flex;
  justify-content: center;
`;
const StructuredResponseWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;
const StructuredSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const StructuredText = styled.div`
  white-space: pre-wrap;
  word-break: break-word;
`;
const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;
const SummaryCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px;
  border-radius: 6px;
  background: ${({ theme }) => theme.themeColors.lowContrastBorder};
`;
const StyledList = styled.ul`
  margin: 0;
  padding-left: 20px;

  li + li {
    margin-top: 8px;
  }
`;
const MethodologyCodeHost = styled.div`
  pre {
    margin: 0;
    padding: 14px 16px 16px;
    overflow-x: auto;
    white-space: pre;
    word-break: normal;
    tab-size: 4;
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
    font-size: 12px;
    line-height: 1.55;
    background-color: ${({ theme }) => theme.themeColors.lowContrastBorder};
    border: 1px solid ${({ theme }) => theme.themeColors.border};
    border-left: 3px solid ${({ theme }) => theme.themeColors.primaryCtaButton};
    border-radius: 6px;
    color: ${({ theme }) => theme.themeColors.text};
  }

  code {
    padding: 0;
    font-family: inherit;
    font-size: inherit;
    line-height: inherit;
  }
`;

const defaultFormState = {
  agentName: '',
  selectedDocs: [],
  tags: [],
  question: '',
  selectedCsvDocs: [],
  noRetrieval: false,
  onlySources: [],
  excludeSources: [],
  invalidCsvDocIds: [],
  csvErrorByDocId: {},
  selectedFeedInputs: [],
  invalidFeedIds: [],
  feedErrorById: {},
};

function Heading() {
  return (
    <HeadingWrapper>
      <Body1 bold>Data Analyst Agent</Body1>
    </HeadingWrapper>
  );
}

const tool = AI_TOOLS.DATA_ANALYST_AGENT;

/** Shown under "Additional Response Fields"; keep this key last so long/raw payloads sit at the bottom. */
const DATA_ANALYST_RESPONSE_ALL_RESULTS_KEY = 'all_results';

const answerConfig = {
  enableMoreDetail: false,
  enableLinks: true,
  ignoreLatex: false,
  enableRating: false,
  enableShare: false,
};

const modeContext = {
  tool,
  questionLabel: 'Request',
  autoSelectDefaultDocuments: false,
  autoSelectRecentDocuments: false,
  enableFollowUpQuestions: true,
  enableSearchForMoreDetail: true,
  inputs: {
    [INPUT_KEYS.question]: true,
  },
  submitEndpoint: 'v1/genai/data_analyst_agent/stream',
  mapSubmitPayload,
};

const formatKeyLabel = (key) =>
  key
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const formatScalarValue = (value) => {
  if (value == null) return 'None';
  if (typeof value === 'boolean') return value ? 'True' : 'False';
  return String(value);
};
const formatPercentValue = (value) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return null;
  const normalizedValue = value <= 1 ? value * 100 : value;
  return `${normalizedValue.toFixed(0)}%`;
};
const CODE_START_RE =
  /\b(?:[A-Za-z_][A-Za-z0-9_]*\s*=|for\s+[A-Za-z_][A-Za-z0-9_]*\s+in|if\s+[A-Za-z_][A-Za-z0-9_]*|import\s+[A-Za-z_]|from\s+[A-Za-z_.]+\s+import)\b/;
const FENCED_CODE_RE = /```(?:python|py)?\s*\n([\s\S]*?)```/;

/** Inserts line breaks before common Python statement starts when the model returns one long line. */
const formatStatementCode = (value) =>
  value
    .replace(/\s+(?=#\s+[A-Za-z#])/g, '\n')
    .replace(
      /\s+(?=(?:for\s+\w+\s+in|while\s+|elif\s+|else:|except|finally:|try:|with\s+|def\s+|class\s+|return\s+|import\s+|from\s+\w|if\s+|df\[|msft\s*=|spy\s*=|required_cols|msft_px|spy_px|prices\s*=|prices\[|rets\s*=|rets\[|if\s+len|if\s+not|x\s*=|y\s*=|x_mean\s*=|y_mean\s*=|cov_xy\s*=|var_x\s*=|beta\s*=|alpha\s*=|corr\s*=|r2\s*=|alpha_annual|window\s*=|rolling_cov|rolling_var|rolling_|norm\s*=|fig\d,\s*ax\d\s*=|ax\d\.|chart\d_path|eq_text|y_line|x_line|y_hat\s*=|ss_res\s*=|ss_tot\s*=|rolling_window\s*=|roll_cov\s*=|roll_var\s*=|result\s*=))/g,
      '\n'
    )
    .replace(/\n{3,}/g, '\n\n')
    .trim();

const prettifyMethodologyCode = (code) => {
  const trimmed = code.trim();
  if (!trimmed) return '';
  const newlineCount = (trimmed.match(/\n/g) || []).length;
  if (newlineCount >= 6) return trimmed;
  return formatStatementCode(trimmed);
};

const wrapPythonMarkdownFence = (code) => {
  const body = code.trimEnd();
  if (!body) return '';
  if (body.includes('```')) {
    return '````\n' + body + '\n````';
  }
  return '```python\n' + body + '\n```';
};

const stripMethodologyLabel = (compactOneLine) =>
  compactOneLine
    .trim()
    .replace(/^#\s*Methodology:?\s*(?:#\s*)?/i, '')
    .replace(/^Methodology#\s*/i, '')
    .replace(/^Methodology:?\s*#?\s*/i, '')
    .trim();

const normalizeMethodologyStepPart = (part) => part.replace(/^#\s*/, '').trim();

const splitMethodologyProseIntoSteps = (proseBody) => {
  const body = proseBody.trim();
  if (!body) return [];

  const hashNumberParts = body
    .split(/\s+#\s*(?=\d+\))/)
    .map(normalizeMethodologyStepPart)
    .filter(Boolean);

  if (hashNumberParts.length && hashNumberParts.every((part) => /^\d+\)/.test(part))) {
    return hashNumberParts;
  }

  const spacedNumberParts = body
    .split(/\s+(?=\d+\)\s)/)
    .map(normalizeMethodologyStepPart)
    .filter(Boolean);

  if (spacedNumberParts.length > 1 && spacedNumberParts.every((part) => /^\d+\)/.test(part))) {
    return spacedNumberParts;
  }

  return body
    .split(/\n+/)
    .map((part) => normalizeMethodologyStepPart(part))
    .filter(Boolean);
};

const parseMethodology = (value) => {
  if (typeof value !== 'string') return null;

  const text = value.replace(/\r\n/g, '\n').trim();
  if (!text) return null;

  let proseSource = text;
  let codeValue = '';

  const fenceMatch = text.match(FENCED_CODE_RE);
  if (fenceMatch) {
    proseSource = text.slice(0, fenceMatch.index).trim();
    codeValue = fenceMatch[1].trim();
  } else {
    const match = text.match(CODE_START_RE);
    const codeStartIndex = match ? match.index : -1;
    proseSource = (codeStartIndex === -1 ? text : text.slice(0, codeStartIndex)).trim();
    codeValue = codeStartIndex === -1 ? '' : text.slice(codeStartIndex).trim();
  }

  const compactProse = proseSource.replace(/\s+/g, ' ').trim();
  const proseBody = stripMethodologyLabel(compactProse);
  const steps = splitMethodologyProseIntoSteps(proseBody);

  return {
    steps,
    code: codeValue ? prettifyMethodologyCode(codeValue) : '',
  };
};

const methodologyHighlightPlugins = [rehypeHighlight];

const MethodologyHighlightedCode = ({ code }) => {
  const markdown = React.useMemo(() => wrapPythonMarkdownFence(code), [code]);

  return (
    <MethodologyCodeHost>
      <MarkdownAnswerStyles as="div">
        <Markdown rehypePlugins={methodologyHighlightPlugins}>{markdown}</Markdown>
      </MarkdownAnswerStyles>
    </MethodologyCodeHost>
  );
};

const ImageList = ({ images }) => {
  if (!Array.isArray(images) || !images.length) {
    return <Body5>No images returned.</Body5>;
  }

  const validImages = images
    .map((image, index) => {
      const imageSrc = getResponseImageSrc(image);
      if (!imageSrc) return null;

      const altText = getResponseImageAltText(image, index);

      return {
        image,
        imageSrc,
        altText,
        key: image.filename || `${altText}-${index}`,
      };
    })
    .filter(Boolean);

  if (!validImages.length) {
    return <Body5>No images returned.</Body5>;
  }

  const isSingleImage = validImages.length === 1;

  return (
    <ImagesWrapper>
      {validImages.map(({ image, imageSrc, altText, key }) => {
        return (
          <ImageCard key={key} $isSingle={isSingleImage}>
            <ResponseImage src={imageSrc} alt={altText} $isSingle={isSingleImage} />
            {image.description ? <Body5>{image.description}</Body5> : null}
            {image.filename ? <Body5 isLowContrast>{image.filename}</Body5> : null}
          </ImageCard>
        );
      })}
    </ImagesWrapper>
  );
};

const QualityAssessmentValue = ({ value }) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return <JsonValue>{JSON.stringify(value, null, 2)}</JsonValue>;
  }

  const summaryItems = [
    { label: 'Quality Score', value: typeof value.quality_score === 'number' ? String(value.quality_score) : null },
    { label: 'Confidence', value: formatPercentValue(value.confidence) },
    {
      label: 'High Quality',
      value: typeof value.is_high_quality === 'boolean' ? formatScalarValue(value.is_high_quality) : null,
    },
    {
      label: 'Needs More Data',
      value: typeof value.needs_more_data === 'boolean' ? formatScalarValue(value.needs_more_data) : null,
    },
  ].filter((item) => item.value != null);

  const listSections = [
    { label: 'Issues', items: value.issues },
    { label: 'Suggestions', items: value.suggestions },
    { label: 'Correct Behaviors', items: value.correct_behaviors },
    { label: 'Data Gaps', items: value.data_gaps },
    { label: 'Suggested Datasets', items: value.suggested_datasets },
  ];

  return (
    <StructuredResponseWrapper>
      {summaryItems.length ? (
        <SummaryGrid>
          {summaryItems.map((item) => (
            <SummaryCard key={item.label}>
              <Body5 bold>{item.label}</Body5>
              <Body5>{item.value}</Body5>
            </SummaryCard>
          ))}
        </SummaryGrid>
      ) : null}

      {value.reasoning ? (
        <StructuredSection>
          <Body5 bold>Reasoning</Body5>
          <StructuredText>
            <Body5>{value.reasoning}</Body5>
          </StructuredText>
        </StructuredSection>
      ) : null}

      {value.thinking ? (
        <StructuredSection>
          <Body5 bold>Thinking</Body5>
          <StructuredText>
            <Body5>{value.thinking}</Body5>
          </StructuredText>
        </StructuredSection>
      ) : null}

      {value.double_checks ? (
        <StructuredSection>
          <Body5 bold>Double Checks</Body5>
          <StructuredText>
            <Body5>{value.double_checks}</Body5>
          </StructuredText>
        </StructuredSection>
      ) : null}

      {listSections.map((section) => {
        if (!Array.isArray(section.items) || !section.items.length) return null;

        return (
          <StructuredSection key={section.label}>
            <Body5 bold>{section.label}</Body5>
            <StyledList>
              {section.items.map((item, index) => (
                <li key={`${section.label}-${index}`}>
                  <Body5>{typeof item === 'string' ? item : JSON.stringify(item)}</Body5>
                </li>
              ))}
            </StyledList>
          </StructuredSection>
        );
      })}
    </StructuredResponseWrapper>
  );
};

const MethodologyValue = ({ value }) => {
  const methodology = parseMethodology(value);

  if (!methodology) {
    return <Body5>{formatScalarValue(value)}</Body5>;
  }

  return (
    <StructuredResponseWrapper>
      {methodology.steps.length ? (
        <StructuredSection>
          <Body5 bold>Approach</Body5>
          <StyledList>
            {methodology.steps.map((step, index) => (
              <li key={`methodology-step-${index}`}>
                <Body5>{step.replace(/^\d+\)\s*/, '')}</Body5>
              </li>
            ))}
          </StyledList>
        </StructuredSection>
      ) : null}

      {methodology.code ? (
        <StructuredSection>
          <Body5 bold>Code Used</Body5>
          <MethodologyHighlightedCode code={methodology.code} />
        </StructuredSection>
      ) : null}
    </StructuredResponseWrapper>
  );
};

const ResponseValue = ({ fieldKey, value, markdownImageLookup }) => {
  if (fieldKey === 'answer') {
    return (
      <AnswerBody
        answer={value}
        enableLinks={answerConfig.enableLinks}
        enableMoreDetail={answerConfig.enableMoreDetail}
        imageSourceLookup={markdownImageLookup}
        ignoreLatex={answerConfig.ignoreLatex}
      />
    );
  }

  if (fieldKey === 'images') {
    return <ImageList images={value} />;
  }

  if (fieldKey === 'quality_assessment') {
    return <QualityAssessmentValue value={value} />;
  }

  if (fieldKey === 'methodology') {
    return <MethodologyValue value={value} />;
  }

  if (typeof value === 'object' && value !== null) {
    return <JsonValue>{JSON.stringify(value, null, 2)}</JsonValue>;
  }

  return <Body5>{formatScalarValue(value)}</Body5>;
};

const SessionTurnCard = ({ turn }) => {
  const [showAdditionalFields, setShowAdditionalFields] = React.useState(false);

  const response = turn.response;
  const markdownImageLookup = React.useMemo(() => buildMarkdownImageLookup(response?.images), [response?.images]);
  const responseEntries = React.useMemo(() => Object.entries(response || {}), [response]);
  const primaryEntries = React.useMemo(() => responseEntries.filter(([key]) => key === 'answer'), [responseEntries]);
  const additionalEntries = React.useMemo(() => {
    const rest = responseEntries.filter(([key]) => key !== 'answer');
    return [...rest].sort(([a], [b]) => {
      if (a === DATA_ANALYST_RESPONSE_ALL_RESULTS_KEY) return 1;
      if (b === DATA_ANALYST_RESPONSE_ALL_RESULTS_KEY) return -1;
      return 0;
    });
  }, [responseEntries]);

  const isLoading = turn.status === 'loading';
  const isError = turn.status === 'error';
  const isCompleted = turn.status === 'completed';

  return (
    <ResponseSectionWrapper>
      <ResponseCard>
        <Body5 bold>Request</Body5>
        <RequestValue>
          {turn.omittedRequest ? (
            <Body5 isLowContrast>Continued — more iterations on prior question.</Body5>
          ) : (
            <Body5>{turn.question}</Body5>
          )}
        </RequestValue>
      </ResponseCard>

      {isLoading ? (
        <ResponseCard>
          <Body5 bold>Response</Body5>
          <Working noVerticalPadding message="Running analysis" />
        </ResponseCard>
      ) : null}

      {isError ? (
        <ResponseCard>
          <Body5 bold>Response</Body5>
          <ErrorText>
            <Body5>{turn.error || 'Something went wrong. Please try again.'}</Body5>
          </ErrorText>
        </ResponseCard>
      ) : null}

      {isCompleted && primaryEntries.length
        ? primaryEntries.map(([key, value]) => (
            <ResponseCard key={key}>
              <Body5 bold>{formatKeyLabel(key)}</Body5>
              <ResponseValue fieldKey={key} value={value} markdownImageLookup={markdownImageLookup} />
            </ResponseCard>
          ))
        : null}

      {isCompleted && additionalEntries.length ? (
        <>
          <AdditionalFieldsToggleWrapper>
            <TransparentFlatButton onClick={() => setShowAdditionalFields(!showAdditionalFields)}>
              {showAdditionalFields
                ? 'Hide Additional Response Fields'
                : `Show Additional Response Fields (${additionalEntries.length})`}
            </TransparentFlatButton>
          </AdditionalFieldsToggleWrapper>

          {showAdditionalFields
            ? additionalEntries.map(([key, value]) => (
                <ResponseCard key={key}>
                  <Body5 bold>{formatKeyLabel(key)}</Body5>
                  <ResponseValue fieldKey={key} value={value} markdownImageLookup={markdownImageLookup} />
                </ResponseCard>
              ))
            : null}
        </>
      ) : null}
    </ResponseSectionWrapper>
  );
};

const SessionTurnsRenderer = ({ turns }) => {
  if (!turns || turns.length === 0) return null;
  return (
    <Container top={24}>
      <HeaderLine isLowContrast />
      <Container top={36}>
        <TurnsList>
          {turns.map((turn) => (
            <SessionTurnCard key={turn.turnId} turn={turn} />
          ))}
        </TurnsList>
      </Container>
    </Container>
  );
};

const fetchUserUploadedDocById = async (id) => {
  const URL = `v1/genai_tickers_filenames?user_uploaded=true`;
  const { status, data } = await sendApiRequest('get', URL);
  logNetRequest(status, data, URL);
  if (status !== 200) return null;
  try {
    const docs = Object.values(data)[0];
    const found = docs.filter((d) => d.id === parseInt(id, 10));
    return found.length > 0 ? found[0] : null;
  } catch (e) {
    logError(e);
    return null;
  }
};

const generateTurnId = () => `turn-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const buildSyntheticTurnFromDetail = (detail) => {
  const turnId = generateTurnId();
  const baseTurn = {
    turnId,
    question: detail.request || '',
    omittedRequest: false,
    daaRecordId: detail.id,
    response: null,
    error: null,
    status: 'completed',
  };
  const result = detail.result;
  if (!result) {
    return { ...baseTurn, status: 'error', error: 'Something went wrong. Please try again.' };
  }
  if (result.error) {
    return { ...baseTurn, status: 'error', error: result.error };
  }
  return { ...baseTurn, response: result };
};

const DataAnalystAgentForm = () => {
  const dispatch = useDispatch();
  const advancedSettingsValues = React.useContext(AdvancedSettingsContext);
  const { state: formState, setFormStateValue } = React.useContext(FormStateContext);
  const {
    state: sessionState,
    startTurn,
    completeTurn,
    failTurn,
    resetSession,
    resumeSession,
  } = React.useContext(SessionContext);
  const [searchParams] = useSearchParams();
  const [kickoffError, setKickoffError] = React.useState(null);
  const [isKickoffInFlight, setIsKickoffInFlight] = React.useState(false);
  const [isPickerOpen, setIsPickerOpen] = React.useState(false);
  const [pendingDetailId, setPendingDetailId] = React.useState(null);
  const pendingDetailIdRef = React.useRef(null);

  const setPendingDetail = React.useCallback((id) => {
    pendingDetailIdRef.current = id;
    setPendingDetailId(id);
  }, []);

  const {
    sessions: continuableSessions,
    isLoadingList,
    listError,
    refetchList,
    fetchDetail: fetchSessionDetail,
    detailErrorById,
  } = useDataAnalystAgentSessions();

  const isSessionActive = sessionState.turns.length > 0;
  const isResumed = selectIsResumed(sessionState);
  const resumedFromId = selectResumedFromId(sessionState);
  const resumedFromCreatedAt = selectResumedFromCreatedAt(sessionState);
  const continueFromRecordId = selectContinueFromRecordId(sessionState);
  const activeLoadingTurn = selectActiveLoadingTurn(sessionState);
  const isTurnInFlight = !!activeLoadingTurn;
  const isBusy = isKickoffInFlight || isTurnInFlight;
  const canShowResumeButton = !isSessionActive && continuableSessions.length > 0 && !listError;

  const activeTurnId = activeLoadingTurn?.turnId || null;
  const activeRecordId = activeLoadingTurn?.daaRecordId || null;

  React.useEffect(() => {
    if (!activeTurnId || !activeRecordId) return undefined;
    const url = `${BASEURL}/api/v1/genai/data_analyst_agent/complete_event?daa_record_id=${activeRecordId}`;
    const source = createAuthedEventSource(url);

    const handleComplete = (ev) => {
      let payload = null;
      try {
        payload = JSON.parse(ev.data);
      } catch (e) {
        logger('[DAA] complete event JSON.parse failed', { rawData: ev.data }, e);
        logError(e);
      }
      const result = payload && payload.response && payload.response.result;
      logNetRequest('data_analyst_agent_complete', 200, result, null);
      if (!result) {
        logger('[DAA] complete event missing response.result', { payload });
        failTurn({ turnId: activeTurnId, error: 'Something went wrong. Please try again.' });
      } else if (result.error) {
        logger('[DAA] worker returned result.error', { error: result.error }, result);
        failTurn({ turnId: activeTurnId, error: result.error });
      } else {
        completeTurn({ turnId: activeTurnId, response: result });
      }
      source.close();
    };

    const handleError = (ev) => {
      try {
        const payload = JSON.parse((ev && ev.data) || 'null');
        if (payload && payload.error) {
          failTurn({ turnId: activeTurnId, error: payload.error });
          source.close();
        }
      } catch (_) {
        // Connection-level drop with no JSON body. Let EventSource auto-reconnect.
      }
    };

    source.addEventListener('complete', handleComplete);
    source.addEventListener('error', handleError);

    return () => {
      source.removeEventListener('complete', handleComplete);
      source.removeEventListener('error', handleError);
      source.close();
    };
  }, [activeTurnId, activeRecordId, completeTurn, failTurn]);

  React.useEffect(() => {
    const csvDocId = searchParams.get('csv-doc-id');
    if (!csvDocId) return undefined;
    const cancel = { isCanceled: false };
    const init = async () => {
      const doc = await fetchUserUploadedDocById(csvDocId);
      if (cancel.isCanceled) return;
      if (!doc) {
        console.warn(`Prefill skipped: doc ${csvDocId} not found`);
        return;
      }
      if (doc.content_type !== 'text/csv') {
        console.warn(`Prefill skipped: doc ${csvDocId} is not a CSV`);
        return;
      }
      setFormStateValue('selectedCsvDocs', [doc]);
    };
    init();
    return () => {
      cancel.isCanceled = true;
    };
  }, []);

  const handleInitialSubmit = React.useCallback(async () => {
    const apiFormData = mapSubmitPayload(formState);
    addAdvancedSettingValues(advancedSettingsValues, apiFormData);

    setIsKickoffInFlight(true);
    setKickoffError(null);
    if ((formState.invalidCsvDocIds || []).length) setFormStateValue('invalidCsvDocIds', []);
    if (Object.keys(formState.csvErrorByDocId || {}).length) setFormStateValue('csvErrorByDocId', {});
    if ((formState.invalidFeedIds || []).length) setFormStateValue('invalidFeedIds', []);
    if (Object.keys(formState.feedErrorById || {}).length) setFormStateValue('feedErrorById', {});

    const { status, data, error } = await sendApiRequest('post', modeContext.submitEndpoint, apiFormData);
    logNetRequest('data_analyst_agent', status, data, error);
    handle400Statuses(status);

    if (status === 200 && data && typeof data.daa_record_id === 'number') {
      startTurn({
        turnId: generateTurnId(),
        question: formState.question,
        omittedRequest: false,
        daaRecordId: data.daa_record_id,
      });
      // The feed rows have been sent and the run's archive now carries them; what stays is the count
      // the summary card shows. Up to five 25,000-row results is real memory to hold for a session.
      const feedItems = formState.selectedFeedInputs || [];
      if (feedItems.length) setFormStateValue('selectedFeedInputs', feedItems.map(stripRows));
      setIsKickoffInFlight(false);
      return;
    }
    const invalid = status === 400 ? invalidIdsFrom400(data) : null;
    if (invalid && (invalid.csvIds.length || invalid.feedIds.length)) {
      if (invalid.csvIds.length) {
        const reason = invalid.reason || 'Invalid CSV document.';
        const errorMap = {};
        invalid.csvIds.forEach((id) => {
          errorMap[id] = reason;
        });
        setFormStateValue('invalidCsvDocIds', invalid.csvIds);
        setFormStateValue('csvErrorByDocId', errorMap);
      }
      if (invalid.feedIds.length) {
        const reason = invalid.reason || FEED_INPUT_COPY.invalidFallback;
        const errorMap = {};
        invalid.feedIds.forEach((id) => {
          errorMap[id] = reason;
        });
        setFormStateValue('invalidFeedIds', invalid.feedIds);
        setFormStateValue('feedErrorById', errorMap);
      }
      setKickoffError(
        invalid.reason || (invalid.csvIds.length ? 'Invalid CSV document.' : FEED_INPUT_COPY.invalidFallback)
      );
    } else {
      const fallbackMsg = (data && data.error) || error;
      if (!fallbackMsg) {
        logger('[DAA] POST kickoff failed with no error message', { status }, { data, error });
      }
      setKickoffError(fallbackMsg || 'Something went wrong. Please try again.');
    }
    setIsKickoffInFlight(false);
  }, [advancedSettingsValues, formState, setFormStateValue, startTurn]);

  // Every way back to the form after a kickoff drops the feed items. CSV docs carry over because
  // they are references; a feed item's rows were released on kickoff, so what is left could not be
  // resent and must not look attachable.
  const clearFeedInputs = React.useCallback(() => {
    setFormStateValue('selectedFeedInputs', []);
    setFormStateValue('invalidFeedIds', []);
    setFormStateValue('feedErrorById', {});
  }, [setFormStateValue]);

  const sendContinuation = React.useCallback(
    async ({ question, omittedRequest, maxIterationsOverride }) => {
      if (!continueFromRecordId) return;

      const payload = { continue_from_record_id: continueFromRecordId };
      if (!omittedRequest && question) payload.request = question;
      if (maxIterationsOverride != null) payload.max_iterations = maxIterationsOverride;
      addAdvancedSettingValues(advancedSettingsValues, payload);

      setIsKickoffInFlight(true);
      setKickoffError(null);

      const { status, data, error } = await sendApiRequest('post', modeContext.submitEndpoint, payload);
      logNetRequest('data_analyst_agent_continue', status, data, error);
      handle400Statuses(status);

      if (status === 200 && data && typeof data.daa_record_id === 'number') {
        startTurn({
          turnId: generateTurnId(),
          question: omittedRequest ? '' : question,
          omittedRequest,
          daaRecordId: data.daa_record_id,
        });
        setIsKickoffInFlight(false);
        return;
      }

      const fallbackMsg = (data && data.error) || error || 'Something went wrong. Please try again.';
      const failedTurnId = generateTurnId();
      startTurn({
        turnId: failedTurnId,
        question: omittedRequest ? '' : question,
        omittedRequest,
        daaRecordId: null,
      });
      failTurn({ turnId: failedTurnId, error: fallbackMsg });
      if (status === 422) {
        resetSession();
        // Back to the form: the feed items left from the kickoff have no rows and must not be
        // offered as attachable again.
        clearFeedInputs();
        setKickoffError('Prior session expired. Please start a new session.');
      }
      setIsKickoffInFlight(false);
    },
    [advancedSettingsValues, clearFeedInputs, continueFromRecordId, failTurn, resetSession, startTurn]
  );

  const handleAskFollowUp = React.useCallback(
    (question) => sendContinuation({ question, omittedRequest: false, maxIterationsOverride: null }),
    [sendContinuation]
  );

  const handleRunMoreIterations = React.useCallback(
    (maxIterationsOverride) => sendContinuation({ question: '', omittedRequest: true, maxIterationsOverride }),
    [sendContinuation]
  );

  const handleStartNewSession = React.useCallback(() => {
    resetSession();
    setKickoffError(null);
    setFormStateValue('question', '');
    clearFeedInputs();
  }, [clearFeedInputs, resetSession, setFormStateValue]);

  const handleReset = React.useCallback(() => {
    setFormStateValue(null);
    setKickoffError(null);
    resetSession();
  }, [setFormStateValue, resetSession]);

  const handleOpenPicker = React.useCallback(() => {
    setIsPickerOpen(true);
  }, []);

  const handleDismissPicker = React.useCallback(() => {
    setPendingDetail(null);
    setIsPickerOpen(false);
  }, [setPendingDetail]);

  const handlePickSession = React.useCallback(
    async (id) => {
      setPendingDetail(id);
      const detail = await fetchSessionDetail(id);

      if (pendingDetailIdRef.current !== id) return;
      if (!detail) {
        setPendingDetail(null);
        return;
      }

      const modelPayload = {};
      if (detail.model) modelPayload.model = detail.model;
      if (detail.code_generation_model) modelPayload.code_generation_model = detail.code_generation_model;
      if (detail.quality_check_model) modelPayload.quality_check_model = detail.quality_check_model;
      if (Object.keys(modelPayload).length > 0) {
        dispatch({ type: advancedSettingsActionTypes.setAdvancedSettings, payload: modelPayload });
      }

      setFormStateValue(null);
      setKickoffError(null);
      resumeSession({
        turn: buildSyntheticTurnFromDetail(detail),
        resumedFromId: detail.id,
        resumedFromCreatedAt: detail.created_at,
      });
      setIsPickerOpen(false);
      setPendingDetail(null);
    },
    [dispatch, fetchSessionDetail, resumeSession, setFormStateValue, setPendingDetail]
  );

  const trimmedQuestion = (formState.question || '').trim();
  const selectedCsvDocs = formState.selectedCsvDocs || [];
  const invalidCsvDocIds = formState.invalidCsvDocIds || [];
  const selectedFeedInputs = formState.selectedFeedInputs || [];
  const invalidFeedIds = formState.invalidFeedIds || [];
  // Only a feed whose rows are still in hand counts as attached data for a NEW submit; a released
  // one is the session card's summary of what was sent.
  const liveFeeds = liveFeedInputs(selectedFeedInputs);
  // Retrieval off means the agent works from attached data alone, and a score feed is attached data.
  const noRetrievalMissingInputs = !!formState.noRetrieval && selectedCsvDocs.length === 0 && liveFeeds.length === 0;
  // An invalid feed id only blocks Submit while a chip carries it: one the server spelled
  // differently, or that outlived its chip, could never be cleared from the form.
  const hasInvalidChips = invalidCsvDocIds.length > 0 || hasInvalidFeedChip(selectedFeedInputs, invalidFeedIds);
  const isSubmitDisabled = isBusy || !trimmedQuestion || noRetrievalMissingInputs || hasInvalidChips;

  if (isSessionActive) {
    const lastTurn = sessionState.turns[sessionState.turns.length - 1];
    const hasCompletedTurn = !!continueFromRecordId;
    const followUpDisabled = !hasCompletedTurn || isBusy;
    let followUpDisabledReason = null;
    if (followUpDisabled) {
      if (isTurnInFlight) {
        followUpDisabledReason = 'Waiting for current analysis to complete.';
      } else if (!hasCompletedTurn && lastTurn && lastTurn.status === 'error') {
        followUpDisabledReason = 'Previous run failed. Start a new session to continue.';
      }
    }

    return (
      <>
        <Heading />
        <SessionWrapper>
          <SessionSummaryCard
            selectedCsvDocs={selectedCsvDocs}
            selectedFeedInputs={selectedFeedInputs}
            noRetrieval={!!formState.noRetrieval}
            onlySources={formState.onlySources || []}
            excludeSources={formState.excludeSources || []}
            onStartNewSession={handleStartNewSession}
            disabled={isBusy}
            isResumed={isResumed}
            resumedFromId={resumedFromId}
            resumedFromCreatedAt={resumedFromCreatedAt}
          />
          <SessionTurnsRenderer turns={sessionState.turns} />
          <FollowUpInput
            disabled={followUpDisabled}
            disabledReason={followUpDisabledReason}
            onAskFollowUp={handleAskFollowUp}
            onRunMoreIterations={handleRunMoreIterations}
          />
          {kickoffError ? (
            <KickoffErrorWrapper>
              <Body5>{kickoffError}</Body5>
            </KickoffErrorWrapper>
          ) : null}
        </SessionWrapper>
      </>
    );
  }

  return (
    <>
      <Heading />
      <FormWrapper>
        {canShowResumeButton ? (
          <ResumeButtonWrapper>
            <SkeletonButton onClick={handleOpenPicker} disabled={isBusy}>
              Resume previous session
            </SkeletonButton>
          </ResumeButtonWrapper>
        ) : null}
        <PrefilledQuestionInput />
        <CsvDocAttachment />
        <FeedDocAttachment />
        <NoRetrievalToggle />
        <SourceFilterSection />
        <FormActionsWrapper>
          <FlatButton fullWidth onClick={handleInitialSubmit} disabled={isSubmitDisabled}>
            Submit
          </FlatButton>
          <TransparentFlatButton fullWidth onClick={handleReset} disabled={isBusy}>
            Reset
          </TransparentFlatButton>
        </FormActionsWrapper>
        {kickoffError ? (
          <KickoffErrorWrapper>
            <Body5>{kickoffError}</Body5>
          </KickoffErrorWrapper>
        ) : null}
      </FormWrapper>
      {isPickerOpen ? (
        <SessionPickerModal
          onDismiss={handleDismissPicker}
          sessions={continuableSessions}
          isLoadingList={isLoadingList}
          listError={listError}
          onRefetch={refetchList}
          pendingDetailId={pendingDetailId}
          detailErrorById={detailErrorById}
          onPick={handlePickSession}
        />
      ) : null}
    </>
  );
};

export function DataAnalystAgent() {
  const navigate = useNavigate();

  const onGoBack = () => navigate(ROUTES.home);

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <Page width={1400}>
          <BackArrowButton onClick={onGoBack} />
          <AdvancedSettingsButton tool={tool} />

          <Container top={40}>
            <ModeContext.Provider value={modeContext}>
              <AdvancedSettingsProvider>
                <FormStateProvider initialStateParam={defaultFormState}>
                  <SessionProvider>
                    <DataAnalystAgentForm />
                  </SessionProvider>
                </FormStateProvider>
              </AdvancedSettingsProvider>
            </ModeContext.Provider>
          </Container>
        </Page>
      </Body>
    </PageWrapper>
  );
}
